import { createAsyncThunk } from "@reduxjs/toolkit";
import { axiosComments as api } from "../../../api/axios";
import ENDPOINTS from "../../../api/endpoints";
import Cookies from "js-cookie";

/* ─────────────────────────────────────────────
   Helpers
───────────────────────────────────────────── */
const normalizeError = (err, fallbackMessage) => {
  const data =
    err?.data ||
    err?.response?.data ||
    { message: err?.message || fallbackMessage };

  return {
    message: data?.message || fallbackMessage,
    status: err?.status || err?.response?.status,
    details: data,
  };
};

const toStr = (v) => (v == null ? undefined : String(v).trim() || undefined);

const resolveStoredActor = () => {
  if (typeof window === "undefined") return null;
  const candidates = [
    window.localStorage.getItem("patientAccessToken"),
    window.sessionStorage.getItem("patientAccessToken"),
    window.localStorage.getItem("doctorAccessToken"),
    window.localStorage.getItem("doctor_token"),
  ].filter(Boolean);

  for (const token of candidates) {
    try {
      const payload = JSON.parse(atob(String(token).split(".")[1] || ""));
      const id = payload?.sub || payload?.id || payload?.userId;
      if (id) return { id: String(id), role: String(payload?.role || "") };
    } catch {
      // Ignore malformed or non-JWT storage values.
    }
  }

  const fallbackId =
    window.localStorage.getItem("userId") ||
    window.localStorage.getItem("id") ||
    window.sessionStorage.getItem("userId") ||
    Cookies.get("userId") ||
    Cookies.get("id") ||
    null;
  return fallbackId ? { id: String(fallbackId), role: "" } : null;
};

/**
 * Returns an integer > 0 or undefined.
 * ✅ Rejects 0, negatives, NaN, empty strings, "null", "undefined".
 */
const isUUID = (v) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(
    String(v).toLowerCase()
  );

const toIntOrUndef = (v) => {
  if (v === null || v === undefined) return undefined;
  const s = String(v).trim();
  if (!s || s.toLowerCase() === "null" || s.toLowerCase() === "undefined") {
    return undefined;
  }
  if (isUUID(s)) return s.toLowerCase();
  const n = Number(s);
  return Number.isFinite(n) && n > 0 ? n : undefined;
};

const extractCommentsArray = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.comments)) return data.comments;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.data?.comments)) return data.data.comments;
  if (Array.isArray(data?.data?.data)) return data.data.data;
  if (Array.isArray(data?.data?.items)) return data.data.items;
  return [];
};

const extractSingleComment = (data) => {
  if (data && typeof data === "object" && !Array.isArray(data)) {
    if (data?.data && typeof data.data === "object") return data.data;
    return data;
  }
  return data;
};

const splitFullName = (fullName) => {
  const raw = String(fullName ?? "").trim();
  if (!raw) return { firstName: "", lastName: "" };
  const parts = raw.split(/\s+/);
  return {
    firstName: parts.shift() || "",
    lastName: parts.join(" "),
  };
};

/* ─────────────────────────────────────────────
   Fetch comments for a post
───────────────────────────────────────────── */
export const fetchCommentsByPost = createAsyncThunk(
  "comments/fetchByPost",
  async (postId, { rejectWithValue, signal, getState }) => {
    try {
      const pid = toStr(postId);
      if (!pid) throw new Error("postId is required");

      const { data } = await api.get(ENDPOINTS.COMMENTS.BY_POST(pid), {
        signal,
      });

      let comments = extractCommentsArray(data);

      /* ✅ Enrich comments with role information */
      try {
        const state = getState();
        const currentDoctor = state?.doctor?.user ?? state?.doctors?.currentDoctor ?? null;
        const authUser = state?.auth?.user ?? null;

        comments = comments.map((c) => {
          if (!c) return c;

          /* Doctor comment enrichment */
          if (c?.doctorId && !c?.user) {
            const doctorName =
              currentDoctor?.fullName ||
              currentDoctor?.name ||
              currentDoctor?.displayName ||
              `${currentDoctor?.firstName || ""} ${currentDoctor?.lastName || ""}`.trim() ||
              "Doctor";
            const parts = splitFullName(doctorName);

            c.user = {
              fullName: doctorName,
              name: doctorName,
              firstName: parts.firstName,
              lastName: parts.lastName,
              avatar: currentDoctor?.avatar || currentDoctor?.photo || null,
              role: "doctor",
            };
          }
          /* Patient comment enrichment */
          else if (c?.userId && !c?.user && authUser) {
            const userIdStr = toStr(c?.userId);
            const authIdStr = authUser?.id != null ? String(authUser.id) : undefined;
            const authMongoStr = authUser?._id != null ? String(authUser._id) : undefined;

            if (userIdStr && (authIdStr === userIdStr || authMongoStr === userIdStr)) {
              c.user = {
                fullName:
                  authUser.fullName ||
                  authUser.name ||
                  `${authUser.firstName || ""} ${authUser.lastName || ""}`.trim(),
                name:
                  authUser.fullName ||
                  authUser.name ||
                  `${authUser.firstName || ""} ${authUser.lastName || ""}`.trim(),
                firstName: authUser.firstName || authUser.name || "",
                lastName: authUser.lastName || "",
                avatar: authUser.avatar || authUser.photo || null,
                role: authUser.role || "user",
              };
            }
          }

          return c;
        });
      } catch (e) {
        console.warn("[fetchCommentsByPost] enrich failed:", e?.message || e);
      }

      return { postId: pid, comments };
    } catch (err) {
      return rejectWithValue(normalizeError(err, "Failed to fetch comments"));
    }
  }
);

/* ─────────────────────────────────────────────
   Create a new comment
───────────────────────────────────────────── */
export const createComment = createAsyncThunk(
  "comments/create",
  async (dto, { rejectWithValue, getState }) => {
    try {
      const content = String(dto?.content ?? "").trim();
      const postId = toStr(dto?.postId);

      if (!content) throw new Error("Content is required");
      if (!postId) throw new Error("postId is required");

      const state = getState();
      const authUser =
        state?.auth?.user ||
        state?.auth?.currentUser ||
        state?.user?.me ||
        state?.user?.currentUser ||
        state?.doctor?.user ||
        state?.doctors?.currentDoctor ||
        null;
      const stateActorId =
        authUser?.id ??
        authUser?._id ??
        authUser?.userId ??
        authUser?.pgId ??
        authUser?.uuid;
      const stateRole = String(
        authUser?.role ?? authUser?.type ?? "",
      ).toLowerCase();
      const storedActor = resolveStoredActor();
      const actorId =
        dto?.doctorId ??
        dto?.userId ??
        stateActorId ??
        storedActor?.id;
      const doctorIdRaw =
        dto?.doctorId ??
        (stateRole.includes("doctor") || storedActor?.role === "doctor"
          ? actorId
          : undefined);
      const doctorIdInt = toIntOrUndef(doctorIdRaw);
      const userIdInt = toIntOrUndef(
        dto?.userId ?? (!stateRole.includes("doctor") ? actorId : undefined),
      );

      const postOwnerIdInt = toIntOrUndef(dto?.postOwnerId);
      const postOwnerIdStr = toStr(dto?.postOwnerId);
      const isAnonymous    = !!dto?.isAnonymous;

      const hasDoctor = !!toStr(doctorIdRaw);
      const hasUser = userIdInt != null ||
        !!toStr(dto?.userId) ||
        (!hasDoctor && !!toStr(actorId));

      if (!hasDoctor && !hasUser) {
        throw new Error("userId or doctorId is required");
      }

      if (hasDoctor && hasUser) {
        console.warn(
          "⚠️ [createComment] doctorId and userId received together — doctorId takes priority.",
          { doctorId: dto?.doctorId, userId: dto?.userId }
        );
      }

      const postOwnerBlock =
        postOwnerIdInt != null
          ? { postOwnerId: postOwnerIdInt }
          : postOwnerIdStr
          ? { postOwnerId: postOwnerIdStr }
          : {};

      const actorBlock = hasDoctor
        ? { doctorId: toStr(doctorIdRaw) }
        : { userId: toStr(dto?.userId ?? actorId) };

      const payload = {
        postId,
        content,
        ...(isAnonymous ? { isAnonymous: true } : {}),
        ...postOwnerBlock,
        ...actorBlock,
      };


      const res = await api.post(ENDPOINTS.COMMENTS.BASE, payload, {
        withCredentials: true,
      });

      const comment = extractSingleComment(res?.data) || null;

      const resolvedPostId =
        toStr(comment?.postId) ||
        toStr(res?.data?.postId) ||
        toStr(res?.data?.data?.postId) ||
        postId;

      try {
        const state = getState();
        const authUser = state?.auth?.user ?? null;
        const currentDoctor = state?.doctor?.user ?? state?.doctors?.currentDoctor ?? null;

        const authIdStr =
          authUser?.id != null ? String(authUser.id) : undefined;
        const authMongoStr =
          authUser?._id != null ? String(authUser._id) : undefined;

        const actorId = hasDoctor
          ? String(doctorIdInt)
          : userIdInt != null
          ? String(userIdInt)
          : toStr(dto?.userId);

        if (
          comment &&
          !comment.user &&
          !isAnonymous &&
          actorId
        ) {
          if (hasDoctor) {
            const doctorName =
              currentDoctor?.fullName ||
              currentDoctor?.name ||
              currentDoctor?.displayName ||
              `${currentDoctor?.firstName || ""} ${currentDoctor?.lastName || ""}`.trim();
            const parts = splitFullName(doctorName);

            comment.user = {
              fullName: doctorName || "Doctor",
              name: doctorName || "Doctor",
              firstName: parts.firstName,
              lastName: parts.lastName,
              avatar: currentDoctor?.avatar || currentDoctor?.photo || null,
              role: "doctor",
            };
          } else if (
            authUser &&
            (authIdStr === actorId || authMongoStr === actorId)
          ) {
            comment.user = {
              fullName:
                authUser.fullName ||
                authUser.name ||
                `${authUser.firstName || ""} ${authUser.lastName || ""}`.trim(),
              name:
                authUser.fullName ||
                authUser.name ||
                `${authUser.firstName || ""} ${authUser.lastName || ""}`.trim(),
              firstName: authUser.firstName || authUser.name || "",
              lastName: authUser.lastName || "",
              avatar: authUser.avatar || authUser.photo || null,
              role: authUser.role || "user",
            };
          }
        }
      } catch (e) {
        console.warn("[commentsThunks] enrich failed:", e?.message || e);
      }

      return { postId: resolvedPostId, comment };
    } catch (err) {
      return rejectWithValue(normalizeError(err, "Failed to create comment"));
    }
  }
);

/* ─────────────────────────────────────────────
   Update a comment
   ✅ FIX: passe doctorId/userId au backend si fournis
───────────────────────────────────────────── */
export const updateComment = createAsyncThunk(
  "comments/update",
  async ({ commentId, postId, content, doctorId, userId }, { rejectWithValue }) => {
    try {
      const cid = toStr(commentId);
      const pid = toStr(postId);
      const txt = String(content ?? "").trim();

      if (!cid) throw new Error("commentId is required");
      if (!pid)  throw new Error("postId is required");
      if (!txt)  throw new Error("Content is required");

      // ✅ Inclure l'acteur si le backend vérifie l'autorisation
      const actorBlock = toStr(doctorId)
        ? { doctorId: toStr(doctorId) }
        : toStr(userId)
        ? { userId: toStr(userId) }
        : {};

      const body = {
        content: txt,
        ...actorBlock,
      };


      const res = await api.patch(ENDPOINTS.COMMENTS.UPDATE(cid), body, {
        withCredentials: true,
      });

      const updated = extractSingleComment(res?.data);

      return {
        commentId: cid,
        postId:    pid,
        content:   updated?.content ?? txt,
      };
    } catch (err) {
      return rejectWithValue(normalizeError(err, "Failed to update comment"));
    }
  }
);

/* ─────────────────────────────────────────────
   Delete a comment
───────────────────────────────────────────── */
export const deleteComment = createAsyncThunk(
  "comments/delete",
  async ({ commentId, postId }, { rejectWithValue }) => {
    try {
      const cid = toStr(commentId);
      const pid = toStr(postId);

      if (!cid) throw new Error("commentId is required");
      if (!pid)  throw new Error("postId is required");

      await api.delete(ENDPOINTS.COMMENTS.DELETE(cid), {
        withCredentials: true,
      });

      return { commentId: cid, postId: pid };
    } catch (err) {
      return rejectWithValue(normalizeError(err, "Failed to delete comment"));
    }
  }
);